import { Outlet } from 'react-router-dom'
import { type ReactNode } from 'react'
import AppSidebar from './Sidebar'
import TopBar from './TopBar'
import { SidebarProvider, SidebarInset } from '../ui/sidebar'
import { PageHeaderProvider } from './PageHeader'
import { BranchFilterProvider } from '../../contexts/BranchFilterContext'
import { NotificationProvider } from '../../contexts/NotificationContext'
import { SessionManager } from '../SessionManager'
import DpaBanner from '../DpaBanner'
import TrialStatusBanner from '../TrialStatusBanner'
import { useTheme } from '../../contexts/ThemeContext'
import { cn } from '../../lib/utils'
import { WhatsNewButton, type WhatsNewApp } from '../WhatsNew'
import MobileTabBar, { type MobileTab } from './MobileTabBar'
import { useIsMobile } from '../../hooks/use-mobile'

/**
 * The "panel" dashboard shell shared by the page / my / signage apps.
 *
 * A canvas (`.app-shell` + a `.app-shell-light` / `.app-shell-dark` variant)
 * holds three floating, rounded sections separated by tiny margins: the
 * sidebar (shadcn `floating` variant), the header bar, and the main content.
 * The shared structural tweaks (`.app-shell`) and the per-mode palettes live in
 * each app's `index.css`; the canvas is always a notch darker than the panels.
 * Apps that need a custom header (e.g. signage's BrandingHeader) pass it via
 * the `header` prop; everything else gets the default TopBar. `whatsNewApp`
 * adds that app's What's New trigger to the default TopBar (it stays hidden
 * until the admin has published a release for the app).
 *
 * The shell follows the user's appearance setting: a light canvas + white
 * panels when `resolved === 'light'`, or the brand-dark canvas + near-black
 * panels when dark. Both variants share the structural `.app-shell` rules
 * (floating sidebar radius, gap canvas, hidden scrollbar); only the colour
 * tokens differ. This is what keeps text and surfaces in the same mode — no
 * dark-on-dark from a panel that ignored the theme.
 */
export default function AppShell(
  { header, whatsNewApp, mobileTabs, bottomDock, bottomEnd, phoneNav, contentTop }: {
    header?: ReactNode
    whatsNewApp?: WhatsNewApp
    /** Phone-only bottom tab bar (see MobileTabBar). Omit to keep the drawer only. */
    mobileTabs?: MobileTab[]
    /** Floats at the bottom centre of the content panel (Pocket's Sales | Ops
     *  switch). The content gets room underneath so the dock never covers it. */
    bottomDock?: ReactNode
    /** Pinned to the bottom end corner of the screen (Pocket's staging stamp). */
    bottomEnd?: ReactNode
    /** Phone navigation that replaces the sidebar drawer and the bottom dock on
     *  phones (Pocket's tabs + mode switch + quick action). */
    phoneNav?: ReactNode
    /** Rendered at the top of the content, above the page (Pocket's section tabs). */
    contentTop?: ReactNode
  } = {},
) {
  const { resolved } = useTheme()
  const isMobile = useIsMobile()
  return (
    <PageHeaderProvider>
      <BranchFilterProvider>
      <NotificationProvider app={whatsNewApp}>
        <div
          // h-dvh, not h-screen: on a phone 100vh is the viewport WITH the
          // browser chrome retracted, so the shell overflowed the visible
          // area by the address bar's height and the page itself scrolled —
          // taking the top bar with it. dvh tracks the visible viewport, so
          // the shell fits and only the content panel scrolls.
          className={cn(
            'app-shell h-dvh overflow-hidden',
            resolved === 'dark' ? 'app-shell-dark dark' : 'app-shell-light',
          )}
        >
          <SidebarProvider defaultOpen className="static flex h-full overflow-hidden bg-transparent">
            <SessionManager />
            <AppSidebar variant="floating" />
            {/* No `h-full` here: the inset is a flex child that already stretches
                to the row's full height, and `m-1` is subtracted from that stretch.
                Adding `h-full` would force 100% + margin, overflowing so the
                ancestor's overflow-hidden clips the content panel. Flex-stretch +
                m-1 sizes it exactly. `m-1` + `gap-1` keep the sidebar↔content and
                header↔content spacing tight so the panels read wider. */}
            <SidebarInset className="min-w-0 min-h-0 flex flex-col gap-1 bg-transparent overflow-hidden m-1">
              {/* Header — its own rounded panel */}
              <div className="shrink-0 rounded-2xl border border-sidebar-border shadow bg-sidebar overflow-hidden">
                {header ?? <TopBar compactOnPhone={!!phoneNav}>{whatsNewApp && <WhatsNewButton app={whatsNewApp} />}</TopBar>}
              </div>
              {/* Content — its own rounded panel. The inner wrapper is a
                  full-height flex column: normal pages flow and the panel
                  scrolls when they outgrow it, while a page that opts into
                  `flex-1 min-h-0` (e.g. the dashboard) is bounded to the panel
                  so only its own inner sections scroll — no page-level scroll,
                  and no fragile 100vh math. */}
              <div data-app-content className="relative flex-1 min-h-0 min-w-0 rounded-2xl border border-sidebar-border shadow bg-sidebar overflow-y-auto">
                {/* h-full (not min-h-full) so a page that opts into `flex-1
                    min-h-0` is clamped to the panel height and can bound its own
                    inner scroll — with min-h-full the wrapper grew with content
                    and the panel itself scrolled. Flowing pages taller than the
                    panel still overflow and scroll the panel as before. */}
                {/* On 2K / ultra-wide desktops the fluid spacing scale caps at a
                    1240px viewport, so without a width cap the content sprawls
                    edge-to-edge. Center it and cap the readable column past
                    ~1728px (kicks in only above the 2xl breakpoint, so laptops
                    and standard desktops are unchanged). */}
                <div className={cn('p-4 sm:p-fluid-l h-full w-full 2xl:max-w-[1728px] 2xl:mx-auto flex flex-col',
                  // Room under the page for what floats over its bottom edge.
                  isMobile && phoneNav ? 'pb-36' : bottomDock && 'pb-24 sm:pb-24')}>
                  <DpaBanner />
                  <TrialStatusBanner />
                  {contentTop}
                  <Outlet />
                </div>
              </div>
              {bottomDock && !(isMobile && phoneNav) && (
                <div className="pointer-events-none fixed inset-x-0 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-40 flex justify-center">
                  <div className="pointer-events-auto">{bottomDock}</div>
                </div>
              )}
              {bottomEnd && (
                <div className="pointer-events-none fixed bottom-[calc(0.5rem+env(safe-area-inset-bottom))] end-3 z-40">
                  <div className="pointer-events-auto">{bottomEnd}</div>
                </div>
              )}
              {isMobile && phoneNav}
              {isMobile && !phoneNav && mobileTabs && mobileTabs.length > 0 && <MobileTabBar tabs={mobileTabs} />}
            </SidebarInset>
          </SidebarProvider>
        </div>
      </NotificationProvider>
      </BranchFilterProvider>
    </PageHeaderProvider>
  )
}
